import { DashCircle, IconProps } from "react-bootstrap-icons";
import { useARSContext } from "../contexts";
import { REMOVE_ARS_ITEM } from "../contexts/interface";

/**
 * Props for the RemoveArsItemButton component.
 */
export interface RemoveArsItemButtonProps extends IconProps {
  /** RFC 6901 JSON Pointer path to the item in ARSContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
  /** Sibling count */
  siblingCount?: number;
}

/**
 * A reusable remove button component for ARS entities.
 *
 * Renders a DashCircle icon with text-danger styling that dispatches
 * a REMOVE_ARS_ITEM action when clicked.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the item in ARSContext state.
 * @param props.disabled - Optional flag to disable the remove action.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the remove button.
 */
export const RemoveArsItemButton = ({
  dataPath,
  disabled = false,
  siblingCount,
  ...iconProps
}: RemoveArsItemButtonProps): React.ReactElement => {
  const { dispatch } = useARSContext();

  /**
   * Handles removing the item.
   */
  const handleRemove = (): void => {
    /* istanbul ignore else -- Disabled buttons do not invoke the click handler */
    if (!disabled) {
      dispatch({
        operation: REMOVE_ARS_ITEM,
        dataPath,
        newLength: siblingCount !== undefined ? siblingCount - 1 : undefined,
      });
    }
  };

  return (
    <DashCircle
      {...iconProps}
      id={`${dataPath.replace(/\//g, "-")}-remove-button`}
      className="text-danger"
      role="button"
      aria-label="Remove item"
      aria-disabled={disabled}
      onClick={disabled ? undefined : handleRemove}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        ...iconProps.style,
      }}
    />
  );
};

RemoveArsItemButton.displayName = "RemoveArsItemButton";
